import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Check, Star, Sparkles, Diamond, Zap } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICON_MAP = {
  free: <Zap className="h-6 w-6" />,
  premium: <Star className="h-6 w-6" />,
  gold: <Crown className="h-6 w-6" />,
  diamond: <Diamond className="h-6 w-6" />,
};

const ACCENT_MAP = {
  free: { ring: '', badge: 'bg-slate-100 text-slate-700', btn: 'bg-slate-200 hover:bg-slate-300 text-slate-700', highlight: false },
  premium: { ring: 'ring-2 ring-sky-400', badge: 'bg-sky-100 text-sky-700', btn: 'bg-sky-500 hover:bg-sky-600 text-white', highlight: true, label: 'POPULAIRE' },
  gold: { ring: '', badge: 'bg-yellow-100 text-yellow-700', btn: 'bg-yellow-500 hover:bg-yellow-600 text-slate-900', highlight: false },
  diamond: { ring: '', badge: 'bg-purple-100 text-purple-700', btn: 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white', highlight: false },
};

const FEATURES_FALLBACK = {
  free: ['10 notifications par mois', 'Canal Web uniquement', 'Recherche d\'offres illimitée', 'Profil candidat'],
  premium: ['50 notifications par mois', 'Web + Email', 'Alertes personnalisées', 'Support prioritaire'],
  gold: ['100 notifications par mois', 'Web + Email + SMS', 'Profil visible par les recruteurs', 'Statistiques détaillées'],
  diamond: ['Notifications illimitées', 'Tous les canaux (Web/Email/SMS/WhatsApp)', 'Profil mis en avant', 'Support VIP 24/7'],
};

const Pricing = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const currentUser = JSON.parse(localStorage.getItem('user') || 'null');

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      const cats = Array.isArray(response.data) ? response.data : response.data.categories || [];
      // Sort: free, premium, gold, diamond
      const order = ['free', 'premium', 'gold', 'diamond'];
      cats.sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
      setCategories(cats);
    } catch (error) {
      toast.error('Erreur lors du chargement des plans');
    } finally {
      setLoading(false);
    }
  };

  const handleChoose = (catId) => {
    if (catId === 'free') {
      navigate(currentUser ? '/dashboard' : '/auth');
      return;
    }
    if (!currentUser) {
      toast.info('Connectez-vous ou créez un compte pour activer un abonnement');
      navigate('/auth');
      return;
    }
    navigate(`/checkout?plan=${catId}`);
  };

  const formatPrice = (cat) => {
    const cost = cat.cost ?? cat.price;
    if (cat.id === 'free' || cost === 0 || cost === null || cost === undefined) return '0';
    return cost.toString();
  };

  const formatDuration = (cat) => {
    const days = cat.validity_days ?? cat.duration_days;
    if (!days || days === 0) return 'Illimité';
    if (days >= 90) return `${Math.floor(days / 30)} mois`;
    if (days >= 30) return '30 jours';
    return `${days} jours`;
  };

  const isCurrentPlan = (catId) => {
    if (!currentUser) return false;
    if (catId === 'free') return !currentUser.category_id || currentUser.category_id === 'free';
    return currentUser.category_id === catId;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium mb-4">
            <Sparkles className="h-4 w-4" />
            Choisissez votre plan
          </div>
          <h1
            className="text-4xl md:text-5xl font-bold text-slate-900 mb-4 tracking-tight"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            data-testid="pricing-title"
          >
            Activez l'abonnement qui vous convient
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Recevez plus d'alertes, sur plus de canaux (Email, SMS, WhatsApp). Payez facilement via MobiPay.cd.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((cat) => {
              const accent = ACCENT_MAP[cat.id] || ACCENT_MAP.free;
              const features = (cat.features && cat.features.length > 0) ? cat.features : (FEATURES_FALLBACK[cat.id] || []);
              const isCurrent = isCurrentPlan(cat.id);
              return (
                <Card
                  key={cat.id}
                  className={`relative p-6 flex flex-col bg-white ${accent.ring} ${accent.highlight ? 'shadow-lg scale-[1.02]' : ''}`}
                  data-testid={`plan-card-${cat.id}`}
                >
                  {accent.label && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-sky-500 text-white shadow">{accent.label}</Badge>
                    </div>
                  )}
                  {isCurrent && (
                    <div className="absolute top-3 right-3">
                      <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">
                        <Check className="h-3 w-3 mr-1" /> Actuel
                      </Badge>
                    </div>
                  )}
                  <div className={`h-12 w-12 rounded-xl flex items-center justify-center mb-4 ${accent.badge}`}>
                    {ICON_MAP[cat.id] || <Zap className="h-6 w-6" />}
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 capitalize mb-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    {cat.name_fr || cat.name || cat.id}
                  </h3>
                  <p className="text-sm text-slate-500 mb-4">{cat.description_fr || cat.description || ''}</p>

                  <div className="mb-5">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-extrabold text-slate-900" data-testid={`plan-price-${cat.id}`}>
                        ${formatPrice(cat)}
                      </span>
                      <span className="text-slate-500 text-sm">
                        / {formatDuration(cat)}
                      </span>
                    </div>
                  </div>

                  <ul className="space-y-2 text-sm text-slate-700 mb-6 flex-1">
                    {features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    className={`w-full ${accent.btn}`}
                    onClick={() => handleChoose(cat.id)}
                    disabled={isCurrent}
                    data-testid={`choose-plan-${cat.id}`}
                  >
                    {isCurrent ? 'Plan actuel' : cat.id === 'free' ? 'Commencer gratuitement' : 'Activer ce plan'}
                  </Button>
                </Card>
              );
            })}
          </div>
        )}

        {/* Trust block */}
        <div className="mt-16 text-center">
          <p className="text-sm text-slate-500 mb-2">Paiement sécurisé via</p>
          <div className="inline-flex items-center gap-2 px-5 py-2 bg-white rounded-full border border-slate-200 shadow-sm">
            <span className="font-bold text-slate-900">MobiPay</span>
            <span className="text-slate-400">·</span>
            <span className="text-sm text-slate-600">Mobile Money RDC</span>
          </div>
        </div>

        {/* FAQ light */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div>
            <h4 className="font-semibold text-slate-900 mb-2">Comment payer ?</h4>
            <p className="text-sm text-slate-600">Via Mobile Money (Orange, Airtel, Vodacom, M-Pesa) en quelques secondes.</p>
          </div>
          <div>
            <h4 className="font-semibold text-slate-900 mb-2">Puis-je annuler ?</h4>
            <p className="text-sm text-slate-600">Aucun engagement. Votre plan reste actif jusqu'à expiration, sans renouvellement automatique.</p>
          </div>
          <div>
            <h4 className="font-semibold text-slate-900 mb-2">Puis-je changer de plan ?</h4>
            <p className="text-sm text-slate-600">Oui, vous pouvez passer à un plan supérieur à tout moment depuis votre tableau de bord.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
